import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'
import { useAuthStore } from '../stores/authStore'

// Tessera OIDC config comes from the build env. Argos is a *public* SPA client:
// Authorization Code + PKCE, no client secret. When the authority/client id are
// unset (offline dev, no Tessera), `oidcEnabled` is false and the app falls back
// to the manual dev-token login — see LoginPage.
const authority = import.meta.env.VITE_OIDC_AUTHORITY
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID
const scope = import.meta.env.VITE_OIDC_SCOPE ?? 'openid profile email offline_access'
const origin = globalThis.location?.origin ?? ''
const redirectUri = import.meta.env.VITE_OIDC_REDIRECT_URI ?? `${origin}/auth/callback`

/** True only when a real Tessera authority + client id are configured. */
export const oidcEnabled = Boolean(authority && clientId)

let manager: UserManager | null = null

export function getUserManager(): UserManager {
  if (!oidcEnabled) {
    throw new Error('OIDC is not configured (VITE_OIDC_AUTHORITY / VITE_OIDC_CLIENT_ID unset)')
  }
  if (!manager) {
    manager = new UserManager({
      authority: authority as string,
      client_id: clientId as string,
      redirect_uri: redirectUri,
      post_logout_redirect_uri: `${origin}/`,
      response_type: 'code',
      scope,
      // Renewal uses the refresh-token grant (no hidden iframe), so no
      // silent_redirect_uri is needed. Endpoints are read from Tessera's
      // /.well-known/openid-configuration. Session lives in localStorage.
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: globalThis.localStorage }),
    })
  }
  return manager
}

/** Push the access token into the store apiClient reads, or clear it. */
function syncToken(user: User | null): void {
  const store = useAuthStore.getState()
  if (user && !user.expired && user.access_token) store.setToken(user.access_token)
  else store.clear()
}

/**
 * Wire the UserManager lifecycle into [useAuthStore] so every API call carries a
 * fresh bearer token, and reconcile any persisted session on boot. Idempotent;
 * call once from main.tsx. No-op when OIDC is disabled (dev fallback owns auth).
 */
export function initAuth(): void {
  if (!oidcEnabled) return
  const um = getUserManager()
  um.events.addUserLoaded((user) => syncToken(user))
  um.events.addUserUnloaded(() => useAuthStore.getState().clear())
  um.events.addAccessTokenExpired(() => {
    void um.signinSilent().catch(() => useAuthStore.getState().clear())
  })
  um.events.addSilentRenewError(() => useAuthStore.getState().clear())
  // Reconcile a stored session: use it if still valid, else try a silent refresh.
  void um
    .getUser()
    .then((user) => {
      if (user && !user.expired) {
        syncToken(user)
        return undefined
      }
      if (user) return um.signinSilent().then(syncToken)
      return undefined
    })
    .catch(() => {
      /* no usable session — RequireAuth will route to /login */
    })
}

/** Begin the Authorization Code + PKCE redirect to Tessera. */
export async function login(returnTo = '/'): Promise<void> {
  await getUserManager().signinRedirect({ state: { returnTo } })
}

/** Complete the redirect callback; returns the in-app path to navigate back to. */
export async function completeLogin(): Promise<string> {
  const user = await getUserManager().signinRedirectCallback()
  syncToken(user)
  const state = user.state as { returnTo?: string } | undefined
  return state?.returnTo ?? '/'
}

/** Clear the local session and, with OIDC on, end the Tessera session too. */
export async function logout(): Promise<void> {
  useAuthStore.getState().clear()
  if (oidcEnabled) await getUserManager().signoutRedirect()
}
